import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Grid, // Keep Grid for overall course section layout
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import SelectOptions from "../../components/ui/SelectOptions";
import { FormGrid } from "../../components/ui/FormGrid"; // Assuming FormGrid is flexible
import { InputField } from "../../components/ui/Inputs";
import { SectionHeader } from "../../components/ui/SectionHeader";
import AcademicIcon from "./../../assets/images/icons/studentdetails.png";

interface Props {
  onNext: () => void;
}

interface Option {
  id: string | number;
  name?: string;
  label?: string;
}

interface Field {
  label: string;
  field_id: string;
  placeholder?: string;
  value: string;
  options?: Option[];
  mainLable?: string;
  action_label?: string;
}

interface Course {
  id: number;
  course_type: string;
  percentage_cgpa_selection: "Percentage" | "CGPA";
  percentage_cgpa_value: string;
  year_of_passing: string;
}

interface AcademicDetailsApiData {
  product_application_form: {
    academic_details_section: {
      overall_education_gap: Field;
      course_type_options: Field;
      percentage_cgpa_value_field: Field; // New field definition for percentage/CGPA value
      year_of_passing_options: Field;
    };
  };
}

const academicApiData: AcademicDetailsApiData = {
  product_application_form: {
    academic_details_section: {
      overall_education_gap: {
        label: "Overall Education Gap (In Months)",
        field_id: "overall_education_gap",
        placeholder: "Enter in Months",
        value: "",
      },
      course_type_options: {
        label: "Course Type",
        field_id: "course_type",
        options: [
          { id: "10th", name: "10th" },
          { id: "12th", name: "12th" },
          { id: "graduation", name: "Graduation" },
          { id: "post_graduation", name: "Post Graduation" },
          { id: "diploma", name: "Diploma" },
        ],
        value: "",
      },
      percentage_cgpa_value_field: {
        label: "Percentage / CGPA",
        field_id: "percentage_cgpa_value",
        placeholder: "Enter Value",
        value: "",
      },
      year_of_passing_options: {
        label: "Year of Passing",
        field_id: "year_of_passing",
        options: Array.from({ length: 30 }, (_, i) => {
          const year = new Date().getFullYear() - i;
          return { id: year.toString(), name: year.toString() };
        }),
        value: "",
      },
    },
  },
};

const AcademicDetails: React.FC<Props> = ({ onNext }) => {
  const {
    overall_education_gap,
    course_type_options,
    percentage_cgpa_value_field,
    year_of_passing_options,
  } = academicApiData.product_application_form.academic_details_section;

  const [overallEducationGap, setOverallEducationGap] = useState<string>(
    overall_education_gap.value
  );

  const [courses, setCourses] = useState<Course[]>([
    {
      id: 1,
      course_type: "",
      percentage_cgpa_selection: "Percentage",
      percentage_cgpa_value: "",
      year_of_passing: "",
    },
  ]);

  const handleOverallEducationGapChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setOverallEducationGap(e.target.value);
  };

  const handleAddCourse = () => {
    setCourses((prevCourses) => [
      ...prevCourses,
      {
        id: prevCourses.length + 1,
        course_type: "",
        percentage_cgpa_selection: "Percentage",
        percentage_cgpa_value: "",
        year_of_passing: "",
      },
    ]);
  };

  const handleCourseChange = (
    index: number,
    field: keyof Omit<Course, "id">,
    value: string | number
  ) => {
    setCourses((prevCourses) =>
      prevCourses.map((course, i) =>
        i === index ? { ...course, [field]: value.toString() } : course
      )
    );
  };

  const renderCourseFields = (course: Course, index: number) => (
    <Box key={course.id}>
      <label className="text-lable">Qualification {index + 1}</label>
      <Grid container spacing={2} alignItems="flex-start">
        {/* Course Type - SelectOptions outside FormGrid */}
        <Grid item xs={12} sm={4}>
          <SelectOptions
            label="Select Course Type"
            value={course.course_type}
            onChange={(val) => handleCourseChange(index, "course_type", val)}
            options={course_type_options.options!.map((opt) => ({
              value: opt.id.toString(),
              label: opt.name || opt.label || "",
            }))}
            isMandatory={false}
          />
        </Grid>

        {/* Percentage / CGPA Input inside FormGrid, Radio outside */}
        <Grid item xs={12} sm={4}>
          <FormGrid
            fullWidth // Pass fullWidth prop here
            fields={[
              {
                // Label is handled by Typography above, not by FormGrid's internal label for this component
                label: percentage_cgpa_value_field.label,
                component: (
                  <InputField
                    fullWidth // Make sure InputField expands full width
                    autoComplete="off"
                    placeholder={`Enter ${course.percentage_cgpa_selection}`}
                    value={course.percentage_cgpa_value}
                    onChange={(e) =>
                      handleCourseChange(
                        index,
                        "percentage_cgpa_value",
                        e.target.value
                      )
                    }
                    variant="outlined"
                  />
                ),
              },
            ]}
          />
        </Grid>

        {/* Year of Passing - SelectOptions outside FormGrid */}
        <Grid item xs={12} sm={4}>
          <SelectOptions
            label="Select Year of Passing"
            value={course.year_of_passing}
            onChange={(val) =>
              handleCourseChange(index, "year_of_passing", val)
            }
            options={year_of_passing_options.options!.map((opt) => ({
              value: opt.id.toString(),
              label: opt.name || opt.label || "",
            }))}
            isMandatory={false}
          />
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box>
      <Box>
        <SectionHeader
          icon={<img src={AcademicIcon} alt="Academic Icon" />}
          title="Academic Details"
          subtitle="Academic Related Details"
          completion="0/9"
        />
      </Box>

      {/* Overall Education Gap - Input inside FormGrid */}
      <Box>
        <FormGrid
          fields={[
            {
              label: overall_education_gap.label,
              component: (
                <InputField
                  fullWidth
                  autoComplete="off"
                  placeholder={overall_education_gap.placeholder}
                  value={overallEducationGap}
                  onChange={handleOverallEducationGapChange}
                  variant="outlined"
                />
              ),
            },
          ]}
        />
      </Box>

      {/* Dynamic Course Sections */}
      {courses.map((course, index) => renderCourseFields(course, index))}

      {/* Add Another Level Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="text"
          onClick={handleAddCourse}
          sx={{ textTransform: "none", fontWeight: "bold" }}
          startIcon={
            <Typography variant="h5" sx={{ lineHeight: 1 }}>
              +
            </Typography>
          }
        >
          Add Another Level
        </Button>
      </Box>
    </Box>
  );
};

export default AcademicDetails;
